/**
 * SmsConsentModal Component
 * AIDLC Administrative Backend
 *
 * Full-screen consent modal for SMS notifications.
 * The "I Consent" button is hidden until the user scrolls to the
 * bottom of the consent text — this ensures they have at least
 * seen the disclosures before agreeing.
 *
 * UX decisions:
 * - Custom modal (not the generic Modal) because we need fine control
 *   over the scroll container and the footer reveal behavior.
 * - Phone input lives inside the modal so consent + number capture
 *   happen in a single atomic step — no back-and-forth.
 * - Decline is always visible so the user never feels trapped.
 * - The scrollable area has an inset shadow to hint "there's more below."
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Button } from './Button';

interface SmsConsentModalProps {
  isOpen: boolean;
  onConsent: (phoneNumber: string) => void;
  onDecline: () => void;
  initialPhoneNumber?: string;
}

export function SmsConsentModal({
  isOpen,
  onConsent,
  onDecline,
  initialPhoneNumber = '',
}: SmsConsentModalProps) {
  const [phoneNumber, setPhoneNumber] = useState(initialPhoneNumber);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setHasScrolledToBottom(false);
      setPhoneNumber(initialPhoneNumber);
      setPhoneError(null);
      document.body.style.overflow = 'hidden';

      // Check if content is short enough that no scrolling is needed
      requestAnimationFrame(() => {
        const el = scrollRef.current;
        if (el) {
          // If the content fits without scrolling, reveal the button immediately
          if (el.scrollHeight <= el.clientHeight + 2) {
            setHasScrolledToBottom(true);
          }
        }
      });
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, initialPhoneNumber]);

  // Escape key handler
  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDecline();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onDecline]);

  // Scroll detection — reveal "I Consent" when user reaches the bottom
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || hasScrolledToBottom) return;

    // Threshold: within 20px of the bottom counts as "reached bottom"
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 20;
    if (atBottom) {
      setHasScrolledToBottom(true);
    }
  }, [hasScrolledToBottom]);

  // Phone number validation — simple E.164-ish check
  const validatePhone = (value: string): boolean => {
    // Strip formatting characters for validation
    const digits = value.replace(/[\s\-().+]/g, '');
    return digits.length >= 10 && digits.length <= 15;
  };

  const handleConsent = () => {
    if (!validatePhone(phoneNumber)) {
      setPhoneError('Enter a valid phone number (e.g. +1 555-123-4567)');
      return;
    }
    setPhoneError(null);
    onConsent(phoneNumber.trim());
  };

  if (!isOpen) return null;

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === overlayRef.current) onDecline();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="sms-consent-title"
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl max-h-[90vh] flex flex-col animate-scale-in">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <h2 id="sms-consent-title" className="text-lg font-semibold text-ldaja-black">
            LDAJA AIDLC Alerts — SMS Consent
          </h2>
          <button
            onClick={onDecline}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1"
            aria-label="Close consent modal"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable consent text */}
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto px-6 py-5 min-h-0"
          style={{
            // Inset shadow at top and bottom to indicate scroll overflow
            boxShadow: 'inset 0 -12px 12px -12px rgba(0,0,0,0.08), inset 0 12px 12px -12px rgba(0,0,0,0.04)',
          }}
        >
          {/* Program description */}
          <div className="space-y-4 text-sm text-gray-600 leading-relaxed">
            <div>
              <h3 className="text-xs font-semibold text-gray-800 uppercase tracking-wide mb-2">
                What You Are Consenting To
              </h3>
              <p>
                By providing your mobile phone number and clicking "I Consent" below, you provide
                your <strong className="text-gray-800">express written consent</strong> to receive
                automated SMS text messages from LDAJA ("Company") through the{' '}
                <strong className="text-gray-800">LDAJA AIDLC Alerts</strong> program. Messages will
                be sent to the phone number you provide from{' '}
                <strong className="text-gray-800">+1 (877) 804-4105</strong> or related numbers.
              </p>
            </div>

            {/* What you will receive */}
            <div>
              <h3 className="text-xs font-semibold text-gray-800 uppercase tracking-wide mb-2">
                What You Will Receive
              </h3>
              <p>
                System alerts, agent status updates, and administrative notifications related to
                the AIDLC platform.
              </p>
            </div>

            {/* Required Disclosures */}
            <div>
              <h3 className="text-xs font-semibold text-gray-800 uppercase tracking-wide mb-2">
                Required Disclosures
              </h3>
              <div className="bg-blue-50/60 border border-blue-100 rounded-lg p-4 space-y-2">
                <DisclosureItem>
                  <strong className="text-gray-700">Message frequency:</strong> Up to 20 messages per month
                </DisclosureItem>
                <DisclosureItem>
                  <strong className="text-gray-700">Message and data rates may apply.</strong> Contact your carrier for details.
                </DisclosureItem>
                <DisclosureItem>
                  Reply <strong className="text-gray-700">STOP</strong> to any message to opt out at any time
                </DisclosureItem>
                <DisclosureItem>
                  Reply <strong className="text-gray-700">HELP</strong> to any message for assistance
                </DisclosureItem>
                <DisclosureItem>
                  Consent to receive SMS is <strong className="text-gray-700">not</strong> a condition
                  of purchase, registration, or use of any LDAJA service or product
                </DisclosureItem>
                <DisclosureItem>
                  Carriers are not liable for delayed or undelivered messages
                </DisclosureItem>
              </div>
            </div>

            {/* Legal links */}
            <div className="flex items-center gap-4 pt-1">
              <a
                href="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-ldaja-cyan hover:text-ldaja-blue transition-colors"
              >
                Privacy Policy
              </a>
              <span className="text-gray-300">|</span>
              <a
                href="/sms-terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-ldaja-cyan hover:text-ldaja-blue transition-colors"
              >
                SMS Terms of Service
              </a>
            </div>

            {/* TCPA notice */}
            <p className="text-xs text-gray-400 leading-relaxed">
              By clicking "I Consent," you provide your express written consent as required under
              the Telephone Consumer Protection Act (TCPA). You confirm that you are the owner or
              authorized user of the mobile number you provide.
            </p>

            {/* Phone number input — inside the scroll area so the user sees it
                after reading disclosures, but above the fold enough to be visible
                when the consent button appears */}
            <div className="pt-2 pb-1">
              <label
                htmlFor="consent-phone"
                className="block text-xs font-semibold text-gray-800 mb-1.5"
              >
                Mobile Phone Number
              </label>
              <input
                id="consent-phone"
                type="tel"
                value={phoneNumber}
                onChange={(e) => {
                  setPhoneNumber(e.target.value);
                  if (phoneError) setPhoneError(null);
                }}
                placeholder="+1 (555) 123-4567"
                autoComplete="tel"
                className={`
                  w-full max-w-xs px-3 py-2 text-sm border rounded-lg bg-white
                  focus:outline-none focus:ring-2 focus:ring-ldaja-blue/30 focus:border-ldaja-blue
                  transition-colors font-mono
                  ${phoneError ? 'border-red-400' : 'border-gray-200'}
                `}
                aria-describedby={phoneError ? 'phone-error' : undefined}
              />
              {phoneError && (
                <p id="phone-error" className="text-xs text-red-500 mt-1" role="alert">
                  {phoneError}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Footer — always visible, but "I Consent" only appears after scrolling */}
        <div className="px-6 py-4 border-t border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-between gap-3">
            <button
              onClick={onDecline}
              className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
            >
              Decline
            </button>

            <div className="flex items-center gap-3">
              {/* Scroll hint — disappears once user has scrolled to bottom */}
              {!hasScrolledToBottom && (
                <span className="text-xs text-gray-400 flex items-center gap-1.5 animate-pulse">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                  Scroll to review all terms
                </span>
              )}

              {/* I Consent — slides in after scroll reaches bottom */}
              <div
                className={`transition-all duration-300 ease-out ${
                  hasScrolledToBottom
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-2 pointer-events-none absolute'
                }`}
              >
                <Button
                  variant="primary"
                  size="md"
                  onClick={handleConsent}
                  disabled={!hasScrolledToBottom}
                >
                  I Consent
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Bullet point in the disclosure box */
function DisclosureItem({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2.5 text-sm text-gray-600">
      <span className="w-1.5 h-1.5 rounded-full bg-ldaja-blue mt-1.5 flex-shrink-0" />
      <span>{children}</span>
    </div>
  );
}
